import React, { useState } from 'react';
import {
  Briefcase,
  ChevronDown,
  ChevronUp,
  FileText,
  Target,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import type { ConsultingAnalysis, StrategicQuestionCategory } from '../../../types/workflowAnalysis';
import { HypothesesSection } from './HypothesesSection';
import { DataSourcesSection } from './DataSourcesSection';
import { RecommendedApproachSection } from './RecommendedApproachSection';
import { DeliverablesSection } from './DeliverablesSection';
import { MermaidDiagram } from './MermaidDiagram';
import { MissingResearchAlert } from './MissingResearchAlert';
import { GraphAnalyticsSection } from './GraphAnalyticsSection';

interface ConsultingAnalysisPanelProps {
  analysis: ConsultingAnalysis;
  className?: string;
  defaultExpanded?: boolean;
}

// Category display configuration
const CATEGORY_CONFIG: Record<StrategicQuestionCategory, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}> = {
  data_integration: {
    label: 'Data Integration',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 border-blue-200',
    description: 'Consolidating data from multiple sources to answer a comprehensive question'
  },
  hypothesis_driven: {
    label: 'Hypothesis-Driven',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50 border-purple-200',
    description: 'Testing specific hypotheses to identify root causes'
  },
  predictive: {
    label: 'Predictive',
    color: 'text-green-700',
    bgColor: 'bg-green-50 border-green-200',
    description: 'Forecasting future outcomes based on historical data and trends'
  },
  comparative: {
    label: 'Comparative',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
    description: 'Benchmarking against peers or standards to identify gaps'
  },
  optimization: {
    label: 'Optimization',
    color: 'text-red-700',
    bgColor: 'bg-red-50 border-red-200',
    description: 'Finding the best solution given constraints and trade-offs'
  },
  scenario: {
    label: 'Scenario',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50 border-cyan-200',
    description: 'Evaluating multiple future scenarios to guide strategic decisions'
  },
  hybrid: {
    label: 'Hybrid',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50 border-indigo-200',
    description: 'Complex analysis requiring multiple analytical approaches'
  }
};

const CategoryBadge: React.FC<{ category: StrategicQuestionCategory }> = ({ category }) => {
  const config = CATEGORY_CONFIG[category];
  if (!config) return null;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${config.bgColor}`}>
      <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
    </div>
  );
};

export const ConsultingAnalysisPanel: React.FC<ConsultingAnalysisPanelProps> = ({
  analysis,
  className = '',
  defaultExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const categoryConfig = CATEGORY_CONFIG[analysis.question_category];

  return (
    <div className={`border border-primary-200 rounded-lg overflow-hidden bg-primary-50/30 ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Briefcase className="w-5 h-5 text-primary-700" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-primary-900">Amplinth Analysis</h3>
            <p className="text-sm text-primary-700">
              Strategic methodology and approach recommendation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CategoryBadge category={analysis.question_category} />
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-primary-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-primary-600" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-6 space-y-8 bg-white">
          {/* Category Description */}
          {categoryConfig && (
            <div className={`p-3 rounded-lg border ${categoryConfig.bgColor}`}>
              <p className={`text-sm ${categoryConfig.color}`}>
                <strong>Analysis Type:</strong> {categoryConfig.description}
              </p>
            </div>
          )}

          {/* Missing Research Alert - Show prominently if there are critical items */}
          {analysis.missing_research && analysis.missing_research.length > 0 && (
            <MissingResearchAlert
              missingResearch={analysis.missing_research}
              researchQualityScore={analysis.research_quality_score}
              researchQualityNotes={analysis.research_quality_notes}
            />
          )}

          {/* Executive Summary */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600" />
              <h4 className="text-lg font-semibold text-neutral-900">Executive Summary</h4>
            </div>
            <p className="text-neutral-700 leading-relaxed">
              {analysis.executive_summary}
            </p>
          </div>

          {/* Key Business Question */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 mb-1">Key Business Question</h4>
                <p className="text-amber-900 font-medium">
                  {analysis.key_business_question}
                </p>
              </div>
            </div>
          </div>

          {/* Flow Visualization - Always show when available */}
          {analysis.show_flow_visualization && analysis.mermaid_diagram && (
            <MermaidDiagram
              diagram={analysis.mermaid_diagram}
              title="Analysis Flow"
            />
          )}

          {/* Hypotheses Section - Conditional based on question category */}
          {analysis.show_hypotheses && analysis.hypotheses && analysis.hypotheses.length > 0 && (
            <HypothesesSection hypotheses={analysis.hypotheses} />
          )}

          {/* Data Sources Section */}
          {analysis.data_sources && (
            <DataSourcesSection dataSources={analysis.data_sources} />
          )}

          {/* Recommended Approach */}
          {analysis.recommended_approach && analysis.recommended_approach.length > 0 && (
            <RecommendedApproachSection phases={analysis.recommended_approach} />
          )}

          {/* Expected Deliverables */}
          {analysis.expected_deliverables && analysis.expected_deliverables.length > 0 && (
            <DeliverablesSection deliverables={analysis.expected_deliverables} />
          )}

          {/* Graph Analytics / Neo4j GDS Recommendations */}
          {analysis.recommended_graph_analytics && (
            <GraphAnalyticsSection graphAnalytics={analysis.recommended_graph_analytics} />
          )}

          {/* Key Success Factors & Potential Risks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Key Success Factors */}
            {analysis.key_success_factors && analysis.key_success_factors.length > 0 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-800">Key Success Factors</h4>
                </div>
                <ul className="space-y-2">
                  {analysis.key_success_factors.map((factor, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-green-700">
                      <span className="text-green-500 mt-1">•</span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Potential Risks */}
            {analysis.potential_risks && analysis.potential_risks.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h4 className="font-medium text-red-800">Potential Risks</h4>
                </div>
                <ul className="space-y-2">
                  {analysis.potential_risks.map((risk, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-red-700">
                      <span className="text-red-500 mt-1">•</span>
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Simple Analysis Indicator */}
          {analysis.is_simple_analysis && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> This is a straightforward data integration question.
                The analysis focuses on consolidating data sources rather than testing hypotheses.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultingAnalysisPanel;
